import React, { useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { ArrowLeft, ListOrdered, DollarSign, Loader2, Save, Package as PackageIcon, Users } from 'lucide-react';
import SEO from '../components/SEO';
import TrainerPackagesPanel from '../components/TrainerPackagesPanel';
import GroupEventsPanel from '../components/GroupEventsPanel';
import { updateTrainerFullProfile } from '../lib/api';
import { toast } from 'sonner';

const PRICE_FIELDS = [
  { key: 'price_personal_training', label: 'Personal Training' },
  { key: 'price_self_defense', label: 'Self-Defense' },
  { key: 'price_jiu_jitsu', label: 'Brazilian Jiu-Jitsu' },
];

const ServicesPricing = () => {
  const { user, refreshUser } = useAuth();
  const p = user?.trainer_profile || {};
  const [form, setForm] = useState({
    price_personal_training: p.price_personal_training ?? '',
    price_self_defense: p.price_self_defense ?? '',
    price_jiu_jitsu: p.price_jiu_jitsu ?? '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    const payload = {};
    for (const f of PRICE_FIELDS) {
      const raw = form[f.key];
      if (raw === '' || raw === null) {
        payload[f.key] = null;
        continue;
      }
      const n = Number(raw);
      if (Number.isNaN(n) || n <= 0) {
        toast.error(`${f.label} price must be a positive number`);
        return;
      }
      payload[f.key] = Math.round(n);
    }
    setSaving(true);
    try {
      await updateTrainerFullProfile({ trainer_profile: payload });
      await refreshUser?.();
      toast.success('Pricing updated');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not save pricing');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-[#18181B] border border-zinc-800 rounded-lg p-6" data-testid="listings-services-panel">
      <div className="flex items-center gap-2 mb-1">
        <DollarSign className="w-5 h-5 text-[#C0A062]" />
        <h2 className="text-lg font-bold text-white">Session pricing</h2>
      </div>
      <p className="text-zinc-500 text-sm mb-6">
        Price per 1-hour session. Leave a field blank to stop offering that service.
      </p>
      <div className="grid sm:grid-cols-3 gap-4 mb-6">
        {PRICE_FIELDS.map((f) => (
          <div key={f.key}>
            <Label className="text-zinc-300 text-xs mb-2 block">{f.label}</Label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500 text-sm">$</span>
              <Input
                type="number"
                min="0"
                value={form[f.key]}
                placeholder="Not offered"
                onChange={(e) => setForm((prev) => ({ ...prev, [f.key]: e.target.value }))}
                className="bg-[#09090B] border-zinc-700 text-white pl-7"
                data-testid={`listing-price-${f.key}`}
              />
            </div>
          </div>
        ))}
      </div>
      <Button onClick={save} disabled={saving} className="btn-primary" data-testid="listing-save-pricing-btn">
        {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
        Save pricing
      </Button>
    </div>
  );
};

const MyListings = () => {
  const navigate = useNavigate();
  const { isTrainer, isAuthenticated, loading } = useAuth();

  if (loading) return null;
  if (!isAuthenticated) return <Navigate to="/login" replace />;
  if (!isTrainer) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#09090B] py-8 px-4 sm:px-6 lg:px-8" data-testid="listings-page">
      <SEO title="My Listings | DojoDash Coach" description="Manage your services, packages, and group events on DojoDash." />
      <div className="max-w-4xl mx-auto">
        <Button
          variant="ghost"
          onClick={() => navigate('/dashboard')}
          className="mb-6 text-zinc-400 hover:text-white"
          data-testid="listings-back-btn"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to dashboard
        </Button>

        <div className="flex items-center gap-3 mb-2">
          <ListOrdered className="w-6 h-6 text-[#C0A062]" />
          <h1 className="text-3xl font-bold text-white">My Listings</h1>
        </div>
        <p className="text-zinc-400 mb-8">Services, packages, and group events you offer to clients.</p>

        <Tabs defaultValue="services" className="w-full">
          <TabsList className="bg-[#18181B] border border-zinc-800 mb-6">
            <TabsTrigger value="services" data-testid="listings-tab-services">
              <DollarSign className="w-4 h-4 mr-1.5" />
              Services &amp; Pricing
            </TabsTrigger>
            <TabsTrigger value="packages" data-testid="listings-tab-packages">
              <PackageIcon className="w-4 h-4 mr-1.5" />
              Packages
            </TabsTrigger>
            <TabsTrigger value="events" data-testid="listings-tab-events">
              <Users className="w-4 h-4 mr-1.5" />
              Group Events
            </TabsTrigger>
          </TabsList>

          <TabsContent value="services">
            <ServicesPricing />
          </TabsContent>
          <TabsContent value="packages" data-testid="listings-packages-panel">
            <TrainerPackagesPanel />
          </TabsContent>
          <TabsContent value="events" data-testid="listings-events-panel">
            <GroupEventsPanel />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default MyListings;
